import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { ManualPaymentMethod } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useDeletePaymentMethod } from '../hooks/use-payment-method-mutations';
import { paymentMethodDeleteErrorKey } from '../schemas/payment-method-rules';

export interface DeletePaymentMethodDialogProps {
  storeId: string;
  method: ManualPaymentMethod | null;
  onOpenChange: (open: boolean) => void;
}

/** Confirms deletion; a method unpaid orders still use answers 409 and the dialog says why. */
export function DeletePaymentMethodDialog({
  storeId,
  method,
  onOpenChange,
}: DeletePaymentMethodDialogProps) {
  const { t } = useTranslation(['paymentMethods', 'common', 'errors']);
  const remove = useDeletePaymentMethod(storeId);
  const message = useErrorMessage();
  const errorKey = remove.error ? paymentMethodDeleteErrorKey(remove.error) : null;
  const requestId = isApiError(remove.error) ? remove.error.requestId : undefined;

  return (
    <AlertDialog
      open={method !== null}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('paymentMethods:delete.title', { name: method?.name ?? '' })}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('paymentMethods:delete.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        {remove.isError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {errorKey ? t(errorKey) : message(remove.error)}
              {requestId ? (
                <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
              ) : null}
            </AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={remove.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={remove.isPending}
            onClick={(event) => {
              // Radix closes on click; stay open until the backend has answered.
              event.preventDefault();
              if (method) remove.mutate(method.id, { onSuccess: () => onOpenChange(false) });
            }}
          >
            {remove.isPending
              ? t('paymentMethods:delete.submitting')
              : t('paymentMethods:delete.submit')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
